import React from 'react';
import { formatDate } from '../../../utils/dateUtils';
import { Establishment } from '../EstablishmentTypes';
import { trancheEffectifsLabels, categorieEntrepriseLabels } from './TableConstants';
import { ClipboardIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import NotesButton from '../../notes/NotesButton';

// Format SIRET with spaces for better readability (XXX XXX XXX XXXXX)
export const formatSiret = (siret: string): string => {
  if (!siret) return '';
  return `${siret.slice(0, 3)} ${siret.slice(3, 6)} ${siret.slice(6, 9)} ${siret.slice(9)}`;
};

// Enhanced Establishment Cell Component
export const EnhancedEstablishmentCell: React.FC<{ 
  establishment: Establishment, 
  copyToClipboard: (text: string) => void,
  openGoogleSearch: (name: string, city: string) => void 
}> = ({ establishment, copyToClipboard, openGoogleSearch }) => (
  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
    <div className="flex items-center justify-between">
      <span>{establishment.name}</span>
      <NotesButton
        siret={establishment.siret}
        companyName={establishment.name}
        variant="icon"
        size="sm"
        className="ml-2"
      />
    </div>
    <div className="flex items-center text-xs text-gray-600 mt-1">
      <span>SIRET: {formatSiret(establishment.siret)}</span>
      <button 
        onClick={() => copyToClipboard(establishment.siret)}
        className="ml-2 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        title="Copier le SIRET"
      >
        <ClipboardIcon className="h-4 w-4 text-gray-500" />
      </button>
      <button 
        onClick={() => openGoogleSearch(establishment.name, establishment.city || '')}
        className="ml-1 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        title="Rechercher sur Google"
      >
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
      </button>
    </div>
    <div className="text-xs text-gray-500 mt-1">
      {establishment.numeroVoie} {establishment.typeVoie} {establishment.libelleVoie}
      {(establishment.numeroVoie || establishment.typeVoie || establishment.libelleVoie) && establishment.postalCode && ', '}<br/>
      {establishment.postalCode} {establishment.city}
    </div>
  </td>
);

// Date Cell Component
export const DateCell: React.FC<{ date: string }> = ({ date }) => (
  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
    {formatDate(date)}
  </td>
);

// NAF Code Cell Component
export const NafCodeCell: React.FC<{ 
  nafCode: string, 
  getNafCodeLabel: (code: string) => string 
}> = ({ nafCode, getNafCodeLabel }) => (
  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
    <div>{nafCode}</div>
    <div className="text-xs text-gray-400 text-wrap">
      {getNafCodeLabel(nafCode)}
    </div>
  </td>
);

// Size Cell Component
export const SizeCell: React.FC<{ 
  trancheEffectifs?: string, 
  categorieEntreprise?: string 
}> = ({ trancheEffectifs, categorieEntreprise }) => (
  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
    {trancheEffectifs && trancheEffectifsLabels[trancheEffectifs as keyof typeof trancheEffectifsLabels] || trancheEffectifs || 'Non renseigné'}
    {categorieEntreprise && (
      <div className="text-xs text-gray-500">
        {categorieEntreprise && categorieEntrepriseLabels[categorieEntreprise as keyof typeof categorieEntrepriseLabels] || categorieEntreprise}
      </div>
    )}
  </td>
);

// Siege Cell Component (headquarters information)
export const SiegeCell: React.FC<{ 
  siege?: Establishment['siege'],
  getNafCodeLabel: (code: string) => string,
  copyToClipboard: (text: string) => void
}> = ({ siege, getNafCodeLabel, copyToClipboard }) => {
  if (!siege) {
    return (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        Information non disponible
      </td>
    );
  }
  
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-gray-600">Siège social</span>
        <NotesButton
          siret={siege.siret}
          companyName={`Siège - ${siege.siret}`}
          variant="icon"
          size="sm"
        />
      </div>
      <div className="flex items-center text-xs text-gray-600 mb-1">
        <span>SIRET: {formatSiret(siege.siret)}</span>
        <button 
          onClick={() => copyToClipboard(siege.siret)}
          className="ml-2 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          title="Copier le SIRET"
        >
          <ClipboardIcon className="h-4 w-4 text-gray-500" />
        </button>
      </div>
      <div className="text-xs text-gray-500 mb-1">
        <span className="font-medium">NAF:</span> {siege.nafCode}
        <div className="text-xs text-gray-400 text-wrap">
          {getNafCodeLabel(siege.nafCode)}
        </div>
      </div>
      <div className="text-xs text-gray-500">
        {siege.numeroVoie} {siege.typeVoie} {siege.libelleVoie}
        {(siege.numeroVoie || siege.typeVoie || siege.libelleVoie) && siege.postalCode && ', '}<br/>
        {siege.postalCode} {siege.city}
      </div>
    </td>
  );
}; 